<template>
  <DemandWidgetContainer
    :demand="demand"
    :disable-delete="disableDelete"
    @delete="$emit('delete', $event)"
  >
    <SelectCipe
      v-if="!hideCipe"
      :value="demand.cipes"
      :cipes="cipes"
      :loading="loadingCipes"
      @input="$emit('input', { field: 'cipes', value: $event })"
    />

    <div class="add-demand-widget__fields">
      <SelectDemandSeverity
        :demand="demand"
        :user-role="userRole"
        @input="$emit('input', { field: 'severity', value: $event })"
      />

      <SelectSeriousness
        v-if="showSeriousnessField"
        class="add-demand-widget__seriousness"
        :demand="demand"
        :user-role="userRole"
        @input="$emit('input', { field: 'seriousness', value: $event })"
      />
    </div>

    <SecondOpinionWidget
      v-if="canRequestSecondOpinion"
      :person-id="personId"
      :specialist-opinion="demand.specialistOpinion"
      :user-role="userRole"
      @input="$emit('input', { field: 'specialistOpinion', value: $event })"
    />

    <DemandRecommendation
      v-if="showDalyaRecommendation"
      :channel-id="channelId"
      :person-id="personId"
      :demand="demand"
      :clinical-record-id="clinicalRecordId"
      @update-dalya-recommendation="onUpdateDalyaRecommendation"
    />
  </DemandWidgetContainer>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import SecondOpinionWidget from '@/components/secondOpinion/organisms/SecondOpinionWidget'
import { DemandRecommendation } from '@/components/shared/atoms/demands'
import {
  DemandWidgetContainer,
  SelectDemandSeverity
} from '@/components/shared/molecules/demands'
import { DEMAND_TYPES } from '@/models/demands'
import { configTypes } from '@/models/featureConfig'

import SelectCipe from '../molecules/demands/SelectCipe.vue'
import SelectSeriousness from '../molecules/demands/SelectSeriousness.vue'

const flagNamespace = 'secondary_attention'
const flagName = 'specialist_opinion_widget_users'

export default {
  name: 'AddDemandWidget',
  components: {
    SelectDemandSeverity,
    SecondOpinionWidget,
    SelectCipe,
    SelectSeriousness,
    DemandRecommendation,
    DemandWidgetContainer
  },
  props: {
    personId: { type: String, required: true },
    demand: {
      type: Object,
      required: true,
      validator: demand => demand.description.id
    },
    disableDelete: {
      type: Boolean
    },
    hideCipe: {
      type: Boolean
    },
    clinicalRecordId: {
      type: String,
      default: null
    },
    channelId: {
      type: String,
      default: null
    },
    showDalyaRecommendation: {
      type: Boolean,
      default: false
    }
  },
  emits: ['delete', 'input'],
  data() {
    const channelName = this.demand?.channel?.name

    return {
      cipes: [],
      loadingCipes: false,
      channelName,
      tooltipText: `Essa informação ficará registrada na <br/>demanda e na tarefa de seguimento. <br/>Escreva quais são as propostas e os próximos <br/>passos para essa demanda do(a) membro(a).`,
      canRequestSecondOpinion: null
    }
  },
  computed: {
    ...mapGetters('user', ['user']),
    ...mapState('permissions', {
      clinicalRecordPermission: state => state.clinicalRecord
    }),
    userRole() {
      return this.user?.role
    },
    followId() {
      return this.demand.follow?.id || ''
    },
    showSeriousnessField() {
      return this.demand?.description?.type !== DEMAND_TYPES.GOAL
    }
  },
  mounted() {
    this.getFeatureFlags()
    this.findCipes()
  },
  methods: {
    ...mapActions('pillars/demands', ['getCipesByHealthCondition']),
    ...mapActions('featureConfig', ['getFeatureFlagById']),
    async findCipes() {
      if (this.hideCipe) {
        return
      }
      this.loadingCipes = true
      this.cipes = await this.getCipesByHealthCondition(
        this.demand.description.id
      )
      this.loadingCipes = false
    },
    async getFeatureFlags() {
      const canRequestSecondOpinion = await this.getFeatureFlagById({
        id: `${flagNamespace}-${flagName}`,
        type: configTypes.LIST
      })

      this.canRequestSecondOpinion =
        canRequestSecondOpinion &&
        this.clinicalRecordPermission.secondOpinion.view
    },
    onUpdateDalyaRecommendation(recommendation) {
      this.$emit('input', {
        field: 'dalyaRecommendation',
        value: recommendation
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.add-demand-widget {
  &__expectations {
    &-name {
      align-items: center;
      display: flex;
      line-height: 1;
      gap: var(--gl-spacing-01);
      margin-bottom: var(--gl-spacing-02);

      label {
        color: var(--gl-color-shades-gray-100);
        font-size: var(--gl-font-size-xxs);
        margin-right: var(--gl-spacing-02);
      }
    }
  }

  &__fields {
    display: flex;
    gap: var(--gl-spacing-06);
    flex-wrap: wrap;
    & > section {
      flex-shrink: 0;
      flex-grow: 1;
    }
  }
}
</style>
